import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useEffect, useState, useRef } from 'react'
import {
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
  isValidPhoneNumber,
} from 'libphonenumber-js'
import examples from 'libphonenumber-js/mobile/examples'
import { RedInfo } from '../components/RedInfo'
import {
  Checkbox,
  DateInput,
  EmailInput,
  Input,
  PasswordInput,
  PhoneInput,
  Select,
} from '../components/form-widgets'
import { Tabs } from '../components/tabs'

const isConsentApplicable = (consent, accountType) =>
  consent.type === null || Number(consent.type) === Number(accountType)

const getAdultBirthDateLimit = () => {
  const date = new Date()
  date.setFullYear(date.getFullYear() - 18)

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-')
}

export const getChildBirthDateMinimum = () => {
  const date = new Date()
  date.setFullYear(date.getFullYear() - 18)
  date.setDate(date.getDate() - 1)

  return [date.getFullYear(), String(date.getMonth() + 1).padStart(2, '0'), String(date.getDate()).padStart(2, '0')].join('-')
}

const getChildBirthDateLimit = () => {
  const date = new Date()
  date.setFullYear(date.getFullYear() - 8)

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-')
}

const getPhoneExample = (phonePrefix) => {
  const country = getCountries().find((countryCode) => {
    try {
      return `+${getCountryCallingCode(countryCode)}` === phonePrefix
    } catch {
      return false
    }
  })
  const phoneNumber = country && getExampleNumber(country, examples)

  return phoneNumber ? phoneNumber.formatNational() : '123 456 789'
}

const getRegistrationSchema = (labels, getApplicableConsents) => {
  const childSchema = z.object({
    firstLastName: z.string().trim().min(1, labels.errorChildName),
    birthDate: z
      .string()
      .min(1, labels.errorBirthDate)
      .refine(
        (birthDate) => !birthDate || birthDate <= getChildBirthDateLimit(),
        labels.errorBirthDate,
      ),
    Country: z.string().min(1, labels.errorCountry),
  })

  return z
    .object({
      accountType: z.enum(['1', '2']),
      firstLastName: z.string().trim().min(1, labels.errorName),
      birthDate: z
        .string()
        .min(1, labels.errorBirthDate)
        .refine(
          (birthDate) =>
            !birthDate || birthDate <= getAdultBirthDateLimit(),
          labels.errorBirthDate,
        ),
      Country: z.string().min(1, labels.errorCountry),
      phonePrefix: z.string().min(1, labels.errorPhone),
      phone: z.string().trim().min(1, labels.errorPhone),
      email: z.string().trim().email(labels.errorEmail),
      password: z.string().min(8, labels.errorPassword),
      passwordConfirmation: z.string().min(1, labels.required),
      Children: z.array(childSchema),
      Consents: z.record(z.boolean().optional()),
    })
    .superRefine((data, context) => {
      if (data.password !== data.passwordConfirmation) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: labels.errorPasswordConfirmation,
          path: ['passwordConfirmation'],
        })
      }

      if (
        data.phonePrefix &&
        data.phone &&
        !isValidPhoneNumber(`${data.phonePrefix}${data.phone}`)
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${labels.errorPhone} ${data.phonePrefix ? labels.errorPhoneExample : ''} ${getPhoneExample(data.phonePrefix)}`,
          path: ['phone'],
        })
      }

      if (data.accountType === '2' && data.Children.length === 0) {
        context.addIssue({
          code: z.ZodIssueCode.too_small,
          minimum: 1,
          type: 'array',
          inclusive: true,
          path: ['Children'],
          message: labels.errorChildren,
        })
      }

      getApplicableConsents(data.accountType)
        .filter((consent) => consent.required && !data.Consents[consent.id])
        .forEach((consent) => {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: labels.errorName,
            path: ['Consents', String(consent.id)],
          })
        })
    })
}

const getParentFields = (labels) => [
  {
    name: 'firstLastName',
    label: labels.firstLastName,
    Component: Input,
    required: true,
  },
  {
    name: 'Country',
    label: labels.country,
    Component: Select,
    required: true,
    info: labels.countryInfo,
  },
  { name: 'phone', label: labels.phone, Component: PhoneInput, required: true },
  {
    name: 'birthDate',
    label: labels.birthDate,
    Component: DateInput,
    required: true,
    maxDate: getAdultBirthDateLimit(),
  },
  { name: 'email', label: labels.email, Component: EmailInput, required: true },
  {
    name: 'password',
    label: labels.password,
    Component: PasswordInput,
    required: true,
  },
  {
    name: 'passwordConfirmation',
    label: labels.passwordConfirmation,
    Component: PasswordInput,
    required: true,
  },
]

const getChildFields = (labels) => [
  {
    name: 'firstLastName',
    label: labels.firstLastName,
    Component: Input,
    required: true,
  },
  {
    name: 'birthDate',
    label: labels.birthDate,
    Component: DateInput,
    required: true,
    maxDate: getChildBirthDateLimit(),
    minDate: getChildBirthDateMinimum(),
  },
  {
    name: 'Country',
    label: labels.country,
    Component: Select,
    required: true,
  },
]

const defaultValues = {
  accountType: '1',
  firstLastName: '',
  birthDate: '',
  Country: 'PL',
  phonePrefix: '+48',
  phone: '',
  email: '',
  password: '',
  passwordConfirmation: '',
  Children: [],
  Consents: {},
}

const RegistrationPage = ({
  submitUrl,
  csrfTokenEndpoint,
  resendActivationEndpoint,
  labels,
  backUrl,
  countryEndpoint,
  locale,
  consents = [],
}) => {
  const endpoint = submitUrl
  const registrationConsents = consents || []
  const registrationSchema = getRegistrationSchema(
    labels,
    (accountType) =>
      registrationConsents.filter((consent) =>
        isConsentApplicable(consent, accountType),
      ),
  )
  const parentFields = getParentFields(labels)
  const childFields = getChildFields(labels)
  const [countryOptions, setCountryOptions] = useState([])
  const [phonePrefixOptions, setPhonePrefixOptions] = useState([])
  const [submitError, setSubmitError] = useState('')
  const [success, setSuccess] = useState(false)
  const [resending, setResending] = useState(false)
  const [resent, setResent] = useState(false)
  const [resendError, setResendError] = useState('')
  const resendInFlight = useRef(false)
  const errorRef = useRef(null)
  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    setError,
    setValue,
    watch,
  } = useForm({
    defaultValues,
    resolver: zodResolver(registrationSchema),
    mode: 'onTouched',
  })
  const { append, fields, remove } = useFieldArray({
    control,
    name: 'Children',
  })
  const accountType = watch('accountType')
  const country = watch('Country')
  const phone = watch('phone')
  const applicableConsents = registrationConsents.filter((consent) =>
    isConsentApplicable(consent, accountType),
  )

  useEffect(() => {
    if (!countryEndpoint) {
      return undefined
    }

    const abortController = new AbortController()

    const loadCountries = async () => {
      try {
        const response = await fetch(countryEndpoint, {
          signal: abortController.signal,
        })

        if (!response.ok) {
          return
        }

        const { data } = await response.json()
        const options = data.map((country) => ({
          value: country.iso2,
          label: country[locale] || country.en || country.pl,
        }))
        setCountryOptions(options)
        setPhonePrefixOptions(
          options.flatMap((option) => {
            try {
              const prefix = `+${getCountryCallingCode(option.value.toUpperCase())}`

              return [{
                value: prefix,
                label: `${option.label} (${prefix})`,
              }]
            } catch {
              return []
            }
          }),
        )
      } catch (error) {
        if (error.name !== 'AbortError') {
          setCountryOptions([])
        }
      }
    }

    loadCountries()

    return () => abortController.abort()
  }, [countryEndpoint, locale])

  useEffect(() => {
    if (phone || !country) {
      return
    }

    try {
      setValue('phonePrefix', `+${getCountryCallingCode(country.toUpperCase())}`)
    } catch {
      // setValue('phonePrefix', '+48')
    }
  }, [country, phone, setValue])

  const renderField = (field, name, error) => {
    const { Component } = field

    return (
      <Controller
        key={name}
        control={control}
        name={name}
        render={({ field: controllerField }) => (
          <Component
            id={name.replace(/\./g, '_')}
            name={controllerField.name}
            label={field.label}
            required={field.required}
            value={controllerField.value}
            onChange={(value) => {
              controllerField.onChange(value)
            }}
            onBlur={controllerField.onBlur}
            error={error?.message}
            info={field?.info}
            maxDate={field?.maxDate}
            minDate={field?.minDate || null}
            {...(Component === Select
              ? {
                options: countryOptions,
                placeholder: labels.countryPlaceholder,
              }
              : {})}
          />
        )}
      />
    )
  }

  useEffect(() => {
    if (submitError && errorRef.current) {
      errorRef.current.tabIndex = 0
      errorRef.current.focus()
    }
  }, [submitError])

  const resendActivation = async () => {
    if (resendInFlight.current || resent) return
    resendInFlight.current = true
    setResending(true)
    setResendError('')
    try {
      const tokenResponse = await fetch(csrfTokenEndpoint, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      })
      if (!tokenResponse.ok) throw new Error()
      const { csrfToken } = await tokenResponse.json()
      if (typeof csrfToken !== 'string' || !csrfToken.trim()) throw new Error()
      const response = await fetch(resendActivationEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'X-CSRF-Token': csrfToken,
          'X-Requested-With': 'XMLHttpRequest',
        },
      })
      if (!response.ok) throw new Error()
      setResent(true)
    } catch {
      setResendError(labels.resendError)
    } finally {
      resendInFlight.current = false
      setResending(false)
    }
  }

  const onSubmit = async ({
    Consents,
    passwordConfirmation: __,
    phonePrefix,
    ...data
  }) => {
    setSubmitError('')

    const dataToSend = {
      ...data,
      phone: `${phonePrefix}${data.phone}`,
      Consents: applicableConsents
        .filter((consent) => Consents[consent.id])
        .map((consent) => consent.id),
    }

    delete dataToSend.accountType

    try {
      const tokenResponse = await fetch(csrfTokenEndpoint, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      })
      if (!tokenResponse.ok) {
        throw new Error(labels.errorSubmit)
      }
      const { csrfToken } = await tokenResponse.json()
      if (typeof csrfToken !== 'string' || !csrfToken.trim()) {
        throw new Error(labels.errorSubmit)
      }
      dataToSend._token = csrfToken

      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(dataToSend),
      })

      if (!response.ok) {
        const responseData = await response.json().catch(() => null)
        const responseErrors = Array.isArray(responseData)
          ? responseData
          : responseData?.errors

        if (Array.isArray(responseErrors) && responseErrors.length > 0) {
          responseErrors.forEach(({ field, message }) => {
            if (field && message) {
              setError(field, { type: 'server', message })
              if (field === 'user_registration') {
                setSubmitError(message)
              }
            }
          })
          return
        }
        setSubmitError(labels.errorSubmit)

        return
      }
      setSuccess(true)
    } catch (error) {
      setSubmitError(error.message)
    }
  }

  useEffect(() => {
    if (success) {
      window.requestAnimationFrame(() => {
        window.scrollToLocomotive?.(0, { immediate: true })
      })
    }
  }, [success])

  return (
    <>
      <header className="page-header is-inview" data-scroll-section>
        {backUrl && (
          <div className="container">
            <a
              href={backUrl}
              className="btn-arrow color-black btn-arrow--right"
            >
              <svg
                className="icon icon--right"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M0 9.00049H16" stroke="black" strokeWidth="2" />
                <path d="M8 1L16 9L8 17" stroke="black" strokeWidth="2" />
              </svg>
              <span>{labels.backToCompetition}</span>
            </a>
          </div>
        )}
        {success && (
          <div className="container">
            <div className="register-success">
              <h1
                aria-live={'polite'}
                role={'alert'}
                className="register-success__title h3"
              >
                {success && labels.successTitle}
              </h1>
              <p
                aria-live={'polite'}
                role={'alert'}
                className="register-success__subtitle"
              >
                {success && labels.successSubtitle}
              </p>
              <p className="register-success__info">
                {!resent && <>
                  {labels.successInfo}{' '}
                  <button type="button" onClick={resendActivation} disabled={resending}>
                    {resending ? labels.submitting : labels.successButton}
                  </button>
                </>}
              </p>
              <p role="status" aria-live="polite">{resent ? labels.resendSuccess : resendError}</p>
            </div>
          </div>
        )}
        {!success && (
          <div className="container header-with-link" data-scroll>
            <h1 className="page-header__title show h3">{labels.title}</h1>
            <div className="header-with-link__button">
              <button
                type="button"
                className="btn btn--border btn--lg"
                onClick={() =>
                  document.dispatchEvent(
                    new CustomEvent('login-modal:open', {
                      detail: { title: 'Zaloguj się, aby wziąć udział' },
                    }),
                  )
                }
              >
                {labels.loginUrl}
              </button>
            </div>
          </div>
        )}
      </header>
      {!success && (
        <section>
          <div className="container register-container">
            <RedInfo className="register-container__right">
              {labels.redInfo}
            </RedInfo>
            <form
              className="register-container__left register-form"
              onSubmit={handleSubmit(onSubmit)}
              noValidate
            >
              {submitError && (
                <p
                  ref={errorRef}
                  role="alert"
                  aria-live={'polite'}
                  className={'login-modal__error'}
                >
                  {submitError}
                </p>
              )}
              <div className="form-register__info form-register__info--sm-m normal">
                {labels.formInfo}
              </div>
              <Tabs
                value={accountType}
                onChange={(value) => {
                  setValue('accountType', value, { shouldValidate: true })
                  if (value === '1') {
                    remove()
                  } else if (fields.length === 0) {
                    append({ firstLastName: '', birthDate: '', Country: 'PL' })
                  }
                }}
                items={[
                  { value: '1', label: labels.type1 },
                  { value: '2', label: labels.type2 },
                ]}
              />
              {accountType === '2' && (
                <>
                  {fields.map((child, index) => (
                    <div key={child.id} className={'register-form__relative'}>
                      <h3
                        className={`register-form__subtitle ${
                          index === 0 ? 'register-form__subtitle--first' : ''
                        }`}
                      >
                        {labels.childData}{' '}
                        {fields.length > 1 && <span>{index + 1}</span>}
                      </h3>
                      {childFields.map((field) =>
                        renderField(
                          field,
                          `Children.${index}.${field.name}`,
                          errors.Children?.[index]?.[field.name],
                        ),
                      )}
                      {fields.length > 1 && (
                        <button
                          type="button"
                          className="btn-remove"
                          onClick={() => remove(index)}
                        >
                          {labels.removeChild}
                        </button>
                      )}
                    </div>
                  ))}
                  {errors.Children?.message && (
                    <p role="alert">{errors.Children.message}</p>
                  )}
                  <button
                    type="button"
                    className="btn-add"
                    onClick={() =>
                      append({ firstLastName: '', birthDate: '', Country: 'PL' })
                    }
                  >
                    <svg
                      width="25"
                      height="49"
                      viewBox="0 0 25 49"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M24.5 0.499999C18.1348 0.499999 12.0303 3.02856 7.52944 7.52944C3.02856 12.0303 0.499999 18.1348 0.499999 24.5C0.499999 30.8652 3.02856 36.9697 7.52943 41.4706C12.0303 45.9714 18.1348 48.5 24.5 48.5"
                        stroke="black"
                      />
                    </svg>
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9.34766 1V17"
                        stroke="#171717"
                        strokeWidth="2"
                        strokeLinecap="square"
                      />
                      <path
                        d="M17 8.65222L1 8.65222"
                        stroke="#171717"
                        strokeWidth="2"
                        strokeLinecap="square"
                      />
                    </svg>

                    <span>{labels.addChild}</span>
                  </button>
                  <h3 className="register-form__subtitle">
                    {labels.parentData}
                  </h3>
                </>
              )}
              {parentFields.map((field) =>
                field.name === 'phone' ? (
                  <fieldset key={field.name} className="register-form__phone">
                    <legend className={'visuallyhidden'}>
                      {labels.errorPhoneLegend}
                    </legend>
                    <Controller
                      control={control}
                      name="phonePrefix"
                      render={({ field: prefixField }) => (
                        <Select
                          id="phonePrefix"
                          name={prefixField.name}
                          required
                          value={prefixField.value}
                          onChange={prefixField.onChange}
                          onBlur={prefixField.onBlur}
                          options={phonePrefixOptions}
                          selectProps={{ 'aria-label': labels.phone }}
                          selectedLabel={prefixField.value}
                          widgetClassName="register-form__phone-prefix"
                          error={errors.phonePrefix?.message}
                          label={labels.errorPhonePrefix}
                          // classNameLabel={'visuallyhidden'}
                        />
                      )}
                    />
                    {renderField(field, field.name, errors[field.name])}
                  </fieldset>
                ) : (
                  renderField(field, field.name, errors[field.name])
                ),
              )}
              {applicableConsents.map((consent) => (
                <Controller
                  key={consent.id}
                  control={control}
                  name={`Consents.${consent.id}`}
                  shouldUnregister
                  render={({ field }) => (
                    <Checkbox
                      id={`consent-${consent.id}`}
                      name={field.name}
                      checked={field.value || false}
                      onChange={field.onChange}
                      required={consent.required}
                      label={consent.text}
                      labelUrl={consent.relatedPageUrl || ''}
                      labelUrlText={consent.textLink || ''}
                      error={errors.Consents?.[consent.id]?.message}
                    />
                  )}
                />
              ))}
              <button
                type="submit"
                className="btn btn--black register-form__btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? labels.submitting : labels.submit}
              </button>
            </form>
          </div>
        </section>
      )}
    </>
  )
}

export default RegistrationPage
